/*
 * Convert Image to Chart — the extracted data, drawn.
 *
 * Renders the geometry `buildChartPreview` produces. This component holds no
 * maths: it maps primitives to SVG elements and nothing else, so a change to
 * how a chart is laid out is a change to chartPreviewModel.ts alone.
 *
 * The SVG carries a viewBox and no width/height, so it fills whatever the
 * sheet gives it — a task pane is resizable and this is the only width the
 * feature ever gets. It is `role="img"` with a generated label rather than a
 * pile of announced shapes: the numbers themselves are in the summary line
 * above it, so a screen reader gets the fact, not 40 unlabelled rects.
 */

import { memo, useMemo } from "react";

import { AXIS_LABEL_SIZE, buildChartPreview, type PreviewShape } from "../services/chartPreviewModel";
import type { ChartRenderKind } from "../services/chartTypeOptions";
import type { EditableChart } from "../services/convertChartApi";
import styles from "./ChartPreview.module.css";

interface ChartPreviewProps {
  readonly chart: EditableChart;
  /** How to draw it — from the selected tile, not from `chart.chartType`. */
  readonly render: ChartRenderKind;
  /** Tile caption, used in the accessible label ("Column chart preview: …"). */
  readonly typeLabel: string;
}

export const CHART_PREVIEW_EMPTY_MESSAGE = "Nothing to preview — the extracted chart has no data points.";

/** One primitive → one SVG element. Split out so the mapping stays a flat,
 *  readable table rather than a switch nested inside JSX. */
function PreviewShapeNode({ shape }: Readonly<{ shape: PreviewShape }>) {
  switch (shape.kind) {
    case "rect":
      return <rect x={shape.x} y={shape.y} width={shape.width} height={shape.height} fill={shape.color} />;
    case "path":
      return (
        <path
          d={shape.d}
          fill={shape.fill ?? "none"}
          fillOpacity={shape.fill ? shape.opacity : undefined}
          stroke={shape.muted ? undefined : (shape.stroke ?? "none")}
          strokeWidth={shape.strokeWidth}
          className={shape.muted ? styles.gridStroke : undefined}
        />
      );
    case "dot":
      return <circle cx={shape.cx} cy={shape.cy} r={shape.r} fill={shape.color} />;
    case "axis":
      return (
        <line
          x1={shape.x1}
          y1={shape.y1}
          x2={shape.x2}
          y2={shape.y2}
          className={shape.muted ? styles.gridStroke : styles.axisStroke}
        />
      );
    case "label":
      return (
        <text x={shape.x} y={shape.y} textAnchor={shape.anchor} fontSize={AXIS_LABEL_SIZE} className={styles.axisLabel}>
          {shape.text}
        </text>
      );
  }
}

/** A stable key per shape. Index-based on purpose: the list is rebuilt whole
 *  on every chart/type change and never reordered in place, so position IS
 *  identity here and a synthesised id would only add noise. */
function shapeKey(shape: PreviewShape, index: number): string {
  return `${shape.kind}-${index}`;
}

/**
 * Memoised on its props: the sheet re-renders on every keystroke in the
 * description field, and re-drawing a 40-category SVG each time is work for
 * nothing. The `chart` prop is memoised by the hook, so the identity check
 * here actually holds.
 */
export const ChartPreview = memo(function ChartPreview({ chart, render, typeLabel }: Readonly<ChartPreviewProps>) {
  const model = useMemo(() => buildChartPreview(chart, render), [chart, render]);

  if (model.empty) {
    return (
      <p className={styles.empty} data-testid="chart-preview-empty">
        {CHART_PREVIEW_EMPTY_MESSAGE}
      </p>
    );
  }

  const label = `${typeLabel} chart preview: ${chart.series.length} series across ${chart.categories.length} categories`;

  return (
    <div className={styles.wrapper} data-testid="chart-preview">
      <svg
        className={styles.canvas}
        viewBox={`0 0 ${model.viewBoxWidth} ${model.viewBoxHeight}`}
        role="img"
        aria-label={label}
      >
        {model.shapes.map((shape, index) => (
          <PreviewShapeNode key={shapeKey(shape, index)} shape={shape} />
        ))}
      </svg>

      {model.legend.length > 0 && (
        <ul className={styles.legend} data-testid="chart-preview-legend">
          {model.legend.map((entry) => (
            <li key={`${entry.label}-${entry.color}`} className={styles.legendItem}>
              <span className={styles.legendSwatch} style={{ backgroundColor: entry.color }} aria-hidden="true" />
              <span className={styles.legendLabel}>{entry.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});
